import React, {
  Dispatch, Ref, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  View, StyleSheet, Text, FlatList, Pressable,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import { STATE_EMPTY_ERROR } from './errorMessages';
import titleCase from '../../../../../components/TitleCase';
import isMobile from '../../../../../components/IsMobile';

const characterResetCountdownInterval: number = 500;

export type State = {
  isoCode: string,
  countryCode: string,
  name: string,
}

interface StateSelectorProps {
  states,
  selectedState: State,
  setSelectedState: Dispatch<SetStateAction<State>>,
  stateInvalid: boolean,
  stateDropdownShown: boolean,
  setStateDropdownShown: Dispatch<SetStateAction<boolean>>,
}

const StateSelector = ({
  states, selectedState, setSelectedState, stateInvalid, stateDropdownShown, setStateDropdownShown,
}: StateSelectorProps) => {
  if (!(states?.length > 0)) return null;

  const statesList: Ref<FlatList> = useRef<FlatList>(null);
  const [charactersPressed, setCharactersPressed] = useState<string>('');
  const [latestCharacter, setLatestCharacter] = useState<string>('');
  let characterResetTimeout: number = 0;
  let countdown = null;

  useEffect(() => {
    if (isMobile) return null;
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', saveCharacter, true);
    // eslint-disable-next-line no-undef
    return window.removeEventListener('keydown', saveCharacter);
  }, []);

  const saveCharacter = (event) => {
    if (!(event.key.length === 1 && /^[a-zA-Z ]*$/.test(event.key))) return;
    setLatestCharacter(event.key.toLowerCase());
  };

  useEffect(() => {
    setCharactersPressed(charactersPressed + latestCharacter);
    clearInterval(countdown);
    characterResetTimeout = 2000;
    countdown = setInterval(() => {
      if (characterResetTimeout >= characterResetCountdownInterval) {
        characterResetTimeout -= characterResetCountdownInterval;
      } else {
        clearTimeout(countdown);
        setCharactersPressed('');
      }
    }, characterResetCountdownInterval);
  }, [latestCharacter]);

  useEffect(() => {
    if (!(charactersPressed?.length > 0)) return;
    let found: boolean = false;
    states.forEach((state: State, index: number) => {
      if (!found && state.name.toLowerCase().indexOf(charactersPressed) === 0) {
        statesList.current?.scrollToIndex({ index, animated: false });
        found = true;
      }
    });
    if (found) return;
    states.forEach((state: State, index: number) => {
      if (!found && state.name.toLowerCase().includes(charactersPressed.toLowerCase())) {
        statesList.current?.scrollToIndex({ index, animated: false });
        found = true;
      }
    });
  }, [charactersPressed]);

  const scrollToSelectedState: () => void = () => {
    statesList?.current?.scrollToIndex({
      index: states.findIndex((state: State) => state.isoCode
        === (selectedState ?? states[0])?.isoCode),
      animated: false,
    });
  };

  return <View style={styles.container}>
    <Pressable
      style={[Style.row, styles.option, styles.dropdownButton, stateInvalid && styles.invalid]}
      onPress={() => setStateDropdownShown(!stateDropdownShown)}
    >
      <Text style={styles.text}>{selectedState?.name ?? 'Choose a state'}</Text>
      <FontAwesome
        size={20}
        color={stateInvalid ? Colors.light.hereworksRed500 : Colors.light.hereworksBlue100}
        name={stateDropdownShown ? 'angle-up' : 'angle-down'}
      />
    </Pressable>
    {stateDropdownShown ? <FlatList
      ref={statesList}
      initialNumToRender={300}
      onScrollToIndexFailed={() => { }}
      onLayout={scrollToSelectedState}
      style={styles.dropdownView}
      data={states}
      renderItem={({ item }) => <Pressable
        style={(state: { hovered: boolean, pressed: boolean }) => ([
          state.hovered && Style.listItemHovered, state.pressed && Style.listItemPressed,
        ])}
        key={`state_option_${item.id}`}
        onPress={() => {
          setSelectedState(item);
          setStateDropdownShown(false);
        }}
      >
        <Text
          style={[
            styles.text, styles.option, selectedState?.isoCode === item.isoCode
            && styles.optionSelected,
          ]}
          selectable={false}
        >
          {titleCase(item?.name)}
        </Text>
      </Pressable>}
    /> : (stateInvalid && <Text style={styles.errorText}>{STATE_EMPTY_ERROR}</Text>)}
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginTop: 4,
    zIndex: 2,
  },
  text: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    alignItems: 'center',
  },
  dropdownButton: {
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    height: 44,
  },
  dropdownView: {
    top: 52,
    maxHeight: 180,
    width: 170,
    borderWidth: 1,
    borderRadius: 6,
    backgroundColor: '#FFF',
    position: 'absolute',
    borderColor: Colors.light.hereworksBlue100,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionSelected: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    color: '#FFF',
  },
  invalid: {
    borderColor: Colors.light.hereworksRed500,
  },
  errorText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksRed500,
  },
});

export default StateSelector;
